import React, { ComponentProps, useCallback, useEffect, useState } from 'react';
import styled from 'styled-components';
import { BasePlugin } from '../BasePlugin/BasePlugin';
import { ComponentPosition } from '../impl';
import { useDataTableQuickReportsContext } from '../../utils/DataTableQuickReportsContext';
import { useDataTableContext } from '../../utils/DataTableContext';
import { OnQuickReportsChanged, QueryModel, SortModel } from '../../utils/DataTableQueryProvider/DataTableQuery.types';
import { DataTableDropdownAction } from '../../DataTableAdvancedElements/DataTableRowActions';
import { SystemIcon } from '../../../Icon';
import Typography from '../../../Typography/Typography';
import { useIntlUds } from '../../../../translations/UdsIntlProvider';
import {
  FileFormat,
  OnExport,
  OnExportSchedule,
  OnScheduleExportDelete
} from '../../DataTableAdvancedElements/DataTableQuickReports/utils/QuickReports.type';
import { DEFAULT_EXPORT_FILE_FORMATS } from '../../DataTableAdvancedElements/DataTableQuickReports/utils/QuickReports.constants';
import { ExportPanelActions } from './impl/panel/ExportPanelActions';
import { DataTableExport } from './impl/toolbar/DataTableExport';
import { ScheduleDeleteExportDialog } from './impl/dialogs/ScheduleDeleteExportDialog';
import { ScheduleExportDialog } from './impl/dialogs/ScheduleExportDialog';
import { ExportDialog } from './impl/dialogs/ExportDialog';
import { EditScheduleDesc } from './impl/panel/EditScheduleDesc';
export { type FileFormat };

interface DataTablePluginExporterProps<Q extends QueryModel = QueryModel, S extends SortModel = SortModel> {
  ExportDialogProps?: { children?: React.ReactNode };
  ScheduleExportDialogProps?: { children?: React.ReactNode };
  DataTableDropdownActionProps?: ComponentProps<typeof DataTableDropdownAction>;
  positionInToolbar?: ComponentPosition;
  positionInQuickPanelMenu?: ComponentPosition;

  onOpen?: () => void;
  /* Use this function if you want display dialog with export options */
  onExport?: OnExport<Q, S>;
  /* Use this param with onExport to limit available export options  */
  exportFileFormats?: FileFormat[];
  /* Return boolean to determined if the modal can be closed */
  onExportSchedule?: OnExportSchedule<Q, S>;
  /* Return boolean to determined if the modal can be closed */
  onScheduleExportDelete?: OnScheduleExportDelete<Q, S>;
}

const DEFAULT_POSITION_IN_TOOLBAR: ComponentPosition = {
  countFrom: 'end',
  index: 2
};
const DEFAULT_POSITION_IN_QP_MENU: ComponentPosition = {
  countFrom: 'end',
  index: 1
};

export function DataTablePluginExporter<Q extends QueryModel = QueryModel, S extends SortModel = SortModel>({
  ScheduleExportDialogProps,
  ExportDialogProps,
  DataTableDropdownActionProps,
  positionInToolbar = DEFAULT_POSITION_IN_TOOLBAR,
  positionInQuickPanelMenu = DEFAULT_POSITION_IN_QP_MENU,
  onExportSchedule,
  exportFileFormats = DEFAULT_EXPORT_FILE_FORMATS,
  onScheduleExportDelete,
  onExport,
  onOpen
}: DataTablePluginExporterProps<Q, S>) {
  const { isFetching } = useDataTableContext();
  const { intl } = useIntlUds();
  const [openExportDialog, setOpenExportDialog] = useState<OnQuickReportsChanged<QueryModel, SortModel> | undefined>();
  const [openScheduleExportDialog, setOpenScheduleExportDialog] = useState<
    OnQuickReportsChanged<QueryModel, SortModel> | undefined
  >();
  const [openDeleteScheduleExportDialog, setOpenDeleteScheduleExportDialog] = useState<
    OnQuickReportsChanged<QueryModel, SortModel> | undefined
  >();
  const {
    setOnExportSchedule,
    setOnExport,
    setOnScheduleExportDelete,
    setExportFileFormats,
    unSavedQuickReport,
    newQuickReportId
  } = useDataTableQuickReportsContext<QueryModel, SortModel>();
  const disableAllActions = isFetching;
  const isUnSavedState = !!unSavedQuickReport || !!newQuickReportId;
  const hasAtLeast1ExportOption = exportFileFormats && exportFileFormats?.length > 0;
  const enableQuickReports = !!onExport;

  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (enableQuickReports) {
      setOnScheduleExportDelete(() => onScheduleExportDelete as OnScheduleExportDelete<QueryModel, SortModel>);
      setExportFileFormats(exportFileFormats);
    }
  }, []);
  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (enableQuickReports) {
      setOnExportSchedule(() => onExportSchedule as OnExportSchedule<QueryModel, SortModel>);
    }
  }, [onExportSchedule]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: exception
  useEffect(() => {
    if (enableQuickReports) {
      setOnExport(() => onExport);
    }
  }, [onExport]);

  const onDeleteSaveClose = useCallback(() => {
    setOpenDeleteScheduleExportDialog?.(undefined);
    setOpenScheduleExportDialog(undefined);
  }, []);

  return (
    <BasePlugin
      id="schedule-export"
      key="schedule-export"
      toolbar={{
        variant: 'middle',
        Component: (
          <DataTableExport
            key="data-table-export"
            ExportDialogProps={ExportDialogProps}
            ScheduleExportDialogProps={ScheduleExportDialogProps}
          />
        ),
        position: positionInToolbar
      }}
      quickViewsDrawerActionMenu={{
        Component: (report) => [
          onExport && report?.uid === openExportDialog?.uid && (
            <ExportDialog<QueryModel, SortModel>
              source="quick-report-drawer"
              exportFileFormats={exportFileFormats}
              onClose={() => setOpenExportDialog(undefined)}
              report={openExportDialog}
              switchToScheduleExport={() => setOpenScheduleExportDialog(report)}
              key={!!openExportDialog ? 'open-export' : 'close-export'}
              onSave={onExport as OnExport<QueryModel, SortModel>}
              {...ExportDialogProps}
            />
          ),
          onExportSchedule && report?.uid === openScheduleExportDialog?.uid && (
            <ScheduleExportDialog<QueryModel, SortModel>
              exportFileFormats={exportFileFormats}
              onClose={() => setOpenScheduleExportDialog(undefined)}
              report={openScheduleExportDialog}
              key={!!openScheduleExportDialog ? 'open-schedule-export' : 'close-schedule-export'}
              onSave={onExportSchedule as OnExportSchedule<QueryModel, SortModel>}
              onExportDelete={() => setOpenDeleteScheduleExportDialog(report)}
              {...ScheduleExportDialogProps}
            />
          ),
          onScheduleExportDelete && report?.uid === openDeleteScheduleExportDialog?.uid && (
            <ScheduleDeleteExportDialog<QueryModel, SortModel>
              onClose={() => setOpenDeleteScheduleExportDialog(undefined)}
              onSaveClose={onDeleteSaveClose}
              report={openDeleteScheduleExportDialog}
              key={!!openDeleteScheduleExportDialog ? 'open-delete-schedule-export' : 'close-delete-schedule-export'}
              onSave={onScheduleExportDelete as OnScheduleExportDelete<QueryModel, SortModel>}
            />
          )
        ]
      }}
      quickViewsDrawerActionMenuContent={{
        Component: (report) => [
          onExport && (
            <DataTableDropdownAction
              disabled={isUnSavedState || disableAllActions}
              key="export"
              iconName="upload"
              dataTest="quick-report-export"
              borderTop
              onClick={() => setOpenExportDialog(report)}
              {...DataTableDropdownActionProps}
            >
              {intl.DataTable.QuickViews.action.export}
            </DataTableDropdownAction>
          ),
          onExportSchedule && !report?.schedule && (
            <DataTableDropdownAction
              disabled={isUnSavedState || disableAllActions}
              key="schedule-export"
              dataTest="schedule-export"
              iconName="calendar_today"
              borderBottom
              onClick={() => setOpenScheduleExportDialog(report)}
              {...DataTableDropdownActionProps}
            >
              <StyledScheduleExports>
                <span>{intl.DataTable.QuickViews.action.scheduleExport.label}</span>
                <StyledTypography variant="body2">
                  {intl.DataTable.QuickViews.action.scheduleExport.description}
                </StyledTypography>
              </StyledScheduleExports>
            </DataTableDropdownAction>
          ),
          onScheduleExportDelete && !!report?.schedule && (
            <DataTableDropdownAction
              disabled={isUnSavedState || disableAllActions}
              key="edit-export-schedule"
              dataTest="edit-scheduled-export"
              iconName="calendar_today"
              borderBottom
              onClick={() => setOpenScheduleExportDialog(report)}
              {...DataTableDropdownActionProps}
            >
              <StyledScheduleExports>
                <span>{intl.DataTable.QuickViews.action.editScheduleExport}</span>
                <StyledTypography variant="body2">
                  <SystemIcon name="repeat" size={14} />
                  <EditScheduleDesc report={report} />
                </StyledTypography>
              </StyledScheduleExports>
            </DataTableDropdownAction>
          )
        ],
        position: positionInQuickPanelMenu
      }}
      quickViewsPanelChipMenu={{
        Component: (report) =>
          hasAtLeast1ExportOption && report ? (
            <ExportPanelActions
              key="export-panel-actions"
              onOpen={onOpen}
              report={report}
              ScheduleExportDialogProps={ScheduleExportDialogProps}
              ExportDialogProps={ExportDialogProps}
            />
          ) : null
      }}
    />
  );
}

const StyledScheduleExports = styled.div`
  display: flex;
  flex-direction: column;
`;

const StyledTypography = styled(Typography)`
  display: flex;
  align-items: flex-start;
  color: ${({ theme }) => theme.colorOnSurfaceVariant};
  gap: ${({ theme }) => theme.spacingSize.s};
`;
